import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getLatestVersion, createVersion } from "../api/mobileapp";

const PLATFORMS = ["ios", "android"];

export default function MobileApp() {
  const { profile } = useOutletContext();
  const isAdmin = profile?.role === "admin";
  const [latest, setLatest] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const results = {};
    for (const p of PLATFORMS) {
      try {
        results[p] = await getLatestVersion(p);
      } catch {
        results[p] = null; // 404 = no versions published yet
      }
    }
    setLatest(results);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <h2 className="page-title">Mobile App</h2>
      <p className="page-sub">Published app versions per platform</p>

      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="grid-2">
          {PLATFORMS.map((p) => (
            <div className="card" key={p}>
              <h2 style={{ textTransform: "capitalize" }}>{p}</h2>
              <p className="desc">Latest published version</p>
              {latest[p] ? (
                <>
                  <div style={{ fontSize: 30, fontWeight: 800 }}>
                    v{latest[p].version}
                  </div>
                  <p style={{ fontSize: 13.5, margin: "8px 0 0" }} className="muted">
                    Min supported: {latest[p].min_supported || "—"}
                    <br />
                    Published: {new Date(latest[p].created_at).toLocaleString()}
                  </p>
                  {latest[p].notes && (
                    <p style={{ fontSize: 13.5, marginTop: 8 }}>{latest[p].notes}</p>
                  )}
                </>
              ) : (
                <div className="empty">No version published yet.</div>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && <PublishVersion onPublished={load} />}
      {!isAdmin && (
        <p className="muted" style={{ fontSize: 13.5 }}>
          Publishing new versions requires the admin role.
        </p>
      )}
    </div>
  );
}

function PublishVersion({ onPublished }) {
  const [form, setForm] = useState({
    platform: "ios",
    version: "",
    min_supported: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setBusy(true);
    try {
      const v = await createVersion({
        platform: form.platform,
        version: form.version,
        min_supported: form.min_supported || null,
        notes: form.notes || null,
      });
      setSuccess(`Published ${v.platform} v${v.version}.`);
      setForm((f) => ({ ...f, version: "", min_supported: "", notes: "" }));
      onPublished();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>Publish new version</h2>
      <p className="desc">Announce a new mobile app release (admin only)</p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={submit}>
        <div className="grid-2">
          <div className="field">
            <label>Platform *</label>
            <select className="input" value={form.platform} onChange={set("platform")}>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
            </select>
          </div>
          <div className="field">
            <label>Version *</label>
            <input className="input" placeholder="1.4.0" value={form.version} onChange={set("version")} required />
          </div>
          <div className="field">
            <label>Minimum supported version</label>
            <input className="input" placeholder="1.2.0" value={form.min_supported} onChange={set("min_supported")} />
          </div>
        </div>
        <div className="field">
          <label>Release notes</label>
          <textarea
            className="input"
            style={{ minHeight: 80, fontFamily: "inherit" }}
            placeholder="What's new in this release…"
            value={form.notes}
            onChange={set("notes")}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Publishing…" : "Publish version"}
        </button>
      </form>
    </div>
  );
}
